import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { Student } from '../student.js';
import { Teacher } from '../teacher.js';

/** Who is asking. Every authorization decision is a function of this and the document in question. */
export interface Subject {
  isServer: boolean;
  /** AdornisUser `_id` of the signed-in (or impersonated) user. */
  userID?: string;
  /** `guest:<uuid>` — taken ONLY from the session (startup.ts `upgrade`), never from an operation parameter. */
  guestID?: string;
  /** Teacher `_id` — ClassRoom / ClassMembership / ExamResultRelease key on this, not on the user id. */
  teacherId?: string;
  /** Student `_id` — same reason. */
  studentId?: string;
  /** The real (admin) user while impersonating — set by @adornis/users' impersonation resolver, never by a client. */
  impersonatingUserID?: string;
}

/**
 * Not memoised on purpose: the client context lives for the whole session, so a cached profile lookup would
 * miss a Teacher/Student profile created mid-session (sign-up) or a login on an existing guest session.
 */
export async function getSubject(): Promise<Subject> {
  if (context.serverContext) return { isServer: true };

  const userID: string | undefined = context.userID || undefined;
  const guestID = context.guestUserID?.startsWith('guest:') ? context.guestUserID : undefined;
  if (!userID) return { isServer: false, guestID };

  const [teacher, student] = await Promise.all([
    (await getRawCollection(Teacher._collectionName)).findOne({ userID }, { projection: { _id: 1 } }),
    (await getRawCollection(Student._collectionName)).findOne({ userID }, { projection: { _id: 1 } }),
  ]);
  return {
    isServer: false,
    userID,
    guestID,
    teacherId: teacher?._id as string | undefined,
    studentId: student?._id as string | undefined,
    impersonatingUserID: context.impersonatingUser || undefined,
  };
}

/**
 * The id that owns userID-keyed documents (Exam, Submission, Conversation, files): the account once signed in,
 * otherwise the session guest. Signing in does NOT keep the guest's access — guest data reaches an account
 * only through `claimGuestConversation`, so what an account sees never depends on the browser tab it uses.
 */
export function ownerIds(subject: Subject): string[] {
  const id = subject.userID ?? subject.guestID;
  return id ? [id] : [];
}
